import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileText, Trash2, Loader2, RotateCw, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useApi, useLoad } from "@/lib/api";
import { useMe, isManager } from "@/lib/me";
import { gbp, fmtDate, fmtMonth } from "@/lib/format";
import { rangeQuery } from "@/lib/dates";
import { PageHead, Empty } from "@/components/Bits";
import DateRange from "@/components/DateRange";
import DeleteDialog from "@/components/DeleteDialog";
import { RefCheck } from "@/components/RefCheck";

function Photo({ doc, page, version }) {
  const api = useApi();
  const [url, setUrl] = useState(null);
  const [err, setErr] = useState(false);
  const ctype = doc.pages?.[page]?.content_type || doc.content_type;
  useEffect(() => {
    let u;
    setUrl(null); setErr(false);
    api("get", `/docs/${doc.id}/image?page=${page}&v=${version}`, null, { responseType: "blob" })
      .then((b) => { u = URL.createObjectURL(b); setUrl(u); }).catch(() => setErr(true));
    return () => u && URL.revokeObjectURL(u);
  }, [doc.id, page, version, api]);
  if (err) return <p className="text-sm text-rose-600">Could not load the photo.</p>;
  if (!url) return <Loader2 className="h-5 w-5 animate-spin text-slate-400" />;
  if (ctype === "application/pdf") return <a href={url} target="_blank" rel="noreferrer" className="text-sm font-semibold text-teal-700 underline" data-testid="doc-pdf-link">Open PDF</a>;
  return <img src={url} alt={`${doc.ref} page ${page + 1}`} className="max-h-[60vh] w-full rounded-xl bg-slate-100 object-contain" data-testid="doc-photo" />;
}

function DocDialog({ doc, onClose, onDeleted, onRotated }) {
  const api = useApi();
  const mgr = isManager(useMe());
  const [page, setPage] = useState(0);
  const [versions, setVersions] = useState((doc?.pages || []).map((p) => p.version || 0));
  const [turning, setTurning] = useState(false);
  const [del, setDel] = useState(false);
  if (!doc) return null;
  const pages = doc.pages || [{ content_type: doc.content_type }];
  const turn = async () => {
    setTurning(true);
    try {
      const r = await api("post", `/docs/${doc.id}/rotate`, { degrees: 90, page });
      setVersions((v) => { const n = [...v]; n[page] = r.version; return n; }); onRotated();
    } catch (e) { toast.error(e.message); } finally { setTurning(false); }
  };
  const label = `${doc.kind === "invoice" ? "Invoice" : "Credit claim"} ${doc.ref} · ${doc.supplier} · ${gbp(doc.amount)}`;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="doc-dialog">
        <DialogHeader><DialogTitle className="font-mono">{doc.ref} · {gbp(doc.amount)}</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-600">{doc.supplier} · {doc.kind === "invoice" ? "Invoice" : "Credit claim"} · {doc.date} · uploaded by {doc.by} {fmtDate(doc.created_at)}</p>
        {doc.dn_no && doc.dn_no !== doc.ref && <p className="text-sm text-slate-600" data-testid="doc-dialog-dn">Delivery note {doc.dn_no}{doc.ref_source === "statement" ? " · invoice number linked from the statement" : ""}</p>}
        {doc.ref_type === "delivery_note" && <p className="rounded-xl bg-sky-50 px-3 py-2 text-sm text-sky-800" data-testid="doc-dialog-dn-only">Saved with a delivery note number only. When the statement is reconciled, the owner approves which invoice number it matches.</p>}
        {mgr && pages.length > 1 && (
          <div className="flex flex-wrap gap-1.5" data-testid="doc-page-tabs">
            {pages.map((_, i) => (
              <Button key={i} size="sm" variant={i === page ? "default" : "outline"} onClick={() => setPage(i)} data-testid={`doc-page-${i}`}>Page {i + 1}</Button>
            ))}
          </div>
        )}
        {mgr && <Photo doc={doc} page={page} version={versions[page] || 0} />}
        {mgr && pages[page]?.content_type !== "application/pdf" && (
          <Button variant="outline" size="sm" className="w-fit" onClick={turn} disabled={turning} data-testid="doc-rotate-button">
            {turning ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <RotateCw className="mr-1 h-4 w-4" />} Rotate {pages.length > 1 ? `page ${page + 1}` : "photo"}
          </Button>
        )}
        {doc.lines?.length > 0 && (
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-500"><th className="py-1">Product</th><th>Qty</th><th>Unit</th><th>VAT</th></tr></thead>
            <tbody>{doc.lines.map((l, i) => <tr key={i} className="border-t border-slate-100"><td className="py-1.5">{l.product}</td><td>{l.qty}</td><td className="font-mono">{gbp(l.unit)}</td><td>{l.vat}%</td></tr>)}</tbody>
          </table>
        )}
        {doc.pages_expected > pages.length && (
          <p className="flex items-center gap-2 rounded-xl bg-amber-50 px-3 py-2 text-sm text-amber-800" data-testid="doc-missing-pages">
            <AlertTriangle className="h-4 w-4" /> The invoice says it has {doc.pages_expected} pages but only {pages.length} {pages.length === 1 ? "was" : "were"} photographed.
          </p>
        )}
        {doc.delete_pending
          ? <p className="rounded-xl bg-rose-50 px-3 py-2 text-sm font-medium text-rose-700" data-testid="doc-delete-pending">Delete requested — waiting for an owner.</p>
          : <Button variant="outline" className="text-rose-700" onClick={() => setDel(true)} data-testid="doc-delete-button"><Trash2 className="mr-1 h-4 w-4" /> Delete</Button>}
        {del && <DeleteDialog item={{ kind: "doc", target: doc.id, label }} onClose={() => setDel(false)} onDone={(st) => { setDel(false); onDeleted(st); }} />}
      </DialogContent>
    </Dialog>
  );
}

const TAG = "rounded px-1.5 py-0.5 text-[10px] font-bold uppercase";
const StmtTag = ({ d }) => {
  const s = d.stmt;
  if (!s || s.state === "awaiting_link") return null;
  if (s.state === "matched") return (
    <>
      <span className={`${TAG} bg-emerald-100 text-emerald-800`} data-testid={`doc-stmt-${d.ref}`}>On {fmtMonth(s.month)} statement</span>
      {s.statement_amount != null && <span className={`${TAG} bg-amber-100 text-amber-800`} data-testid={`doc-stmt-diff-${d.ref}`}>Statement shows {gbp(s.statement_amount)}</span>}
    </>
  );
  if (s.state === "missing") return <span className={`${TAG} bg-rose-100 text-rose-700`} data-testid={`doc-stmt-${d.ref}`}>Not on {fmtMonth(s.month)} statement</span>;
  return <span className={`${TAG} bg-slate-100 text-slate-600`} data-testid={`doc-stmt-${d.ref}`}>No statement yet</span>;
};

export default function DocumentsPage() {
  const me = useMe();
  const [range, setRange] = useState({ key: "all", from: null, to: null });
  const [kind, setKind] = useState("");
  const [q, setQ] = useState("");
  const params = [new URLSearchParams(Object.entries({ kind, q }).filter(([, v]) => v)).toString(), rangeQuery(range)].filter(Boolean).join("&");
  const list = useLoad(`/docs?${params}`);
  const [open, setOpen] = useState(null);
  const [stmt, setStmt] = useState("");
  const shown = (list.data || []).filter((d) => !stmt || (stmt === "awaiting_link" ? d.ref_type === "delivery_note" && d.stmt?.state === "awaiting_link" : d.stmt?.state === stmt));
  const total = shown.reduce((t, d) => t + (d.kind === "invoice" ? d.amount : 0), 0);
  return (
    <div>
      <PageHead title="Documents" sub={isManager(me) ? "Every invoice and credit claim uploaded by the team." : "Invoices and credit claims you've uploaded."} />
      {isManager(me) && <RefCheck onDone={list.reload} />}
      <div className="mb-4 grid gap-2 sm:grid-cols-[1fr_minmax(0,1.6fr)_10rem_12rem]">
        <Input placeholder="Search number…" value={q} onChange={(e) => setQ(e.target.value)} className="bg-white" data-testid="docs-search-input" />
        <DateRange value={range} onChange={setRange} testid="docs-range" />
        <select value={kind} onChange={(e) => setKind(e.target.value)} className="h-9 rounded-md border border-input bg-white px-3 text-sm" data-testid="docs-kind-filter">
          <option value="">All types</option><option value="invoice">Invoices</option><option value="credit_claim">Credit claims</option>
        </select>
        <select value={stmt} onChange={(e) => setStmt(e.target.value)} className="h-9 rounded-md border border-input bg-white px-3 text-sm" data-testid="docs-stmt-filter">
          <option value="">Any statement match</option><option value="missing">Not on statement</option><option value="waiting">No statement yet</option>
          <option value="matched">On a statement</option><option value="awaiting_link">Delivery note to link</option>
        </select>
      </div>
      {list.data?.length > 0 && <p className="mb-2 text-xs text-slate-500" data-testid="docs-count">{shown.length} document{shown.length === 1 ? "" : "s"} · invoices {gbp(total)}</p>}
      {list.data?.length === 0 && <Empty icon={FileText} title="No documents" text="Uploads will appear here." testid="docs-empty" />}
      {list.data?.length > 0 && !shown.length && <Empty icon={FileText} title="Nothing here" text="No documents match this statement filter." testid="docs-filter-empty" />}}
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white" data-testid="docs-list">
        {shown.map((d) => (
          <button key={d.id} onClick={() => setOpen(d)} className="flex w-full items-center gap-4 border-b border-slate-100 px-4 py-3 text-left last:border-0 hover:bg-slate-50" data-testid={`doc-row-${d.ref}`}>
            <span className={`h-2 w-2 shrink-0 rounded-full ${d.kind === "invoice" ? "bg-slate-900" : "bg-teal-500"}`} />
            <div className="min-w-0 flex-1"><p className="font-mono text-sm font-semibold">{d.ref}{d.dn_no && d.dn_no !== d.ref && <span className="ml-2 text-xs font-normal text-slate-500" data-testid={`doc-dn-${d.ref}`}>DN {d.dn_no}</span>}</p><p className="truncate text-xs text-slate-500">{d.supplier} · {d.kind === "invoice" ? "Invoice" : "Credit claim"} · {d.date}{d.pages?.length > 1 ? ` · ${d.pages.length} pages` : ""}</p>
              {(d.pages_expected > (d.pages?.length || 1) || d.delete_pending || d.ref_type === "delivery_note" || d.stmt) && (
                <p className="mt-1 flex flex-wrap gap-1">
                  <StmtTag d={d} />
                  {d.ref_type === "delivery_note" && <span className="rounded bg-sky-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-sky-800" data-testid={`doc-dn-badge-${d.ref}`}>Delivery note · invoice no. to link</span>}
                  {d.pages_expected > (d.pages?.length || 1) && <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-amber-800" data-testid={`doc-missing-badge-${d.ref}`}>Page missing</span>}
                  {d.delete_pending && <span className="rounded bg-rose-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-rose-700" data-testid={`doc-pending-badge-${d.ref}`}>Delete requested</span>}
                </p>
              )}</div>
            <p className="font-mono text-sm font-semibold">{gbp(d.amount)}</p>
          </button>
        ))}
      </div>
      <DocDialog key={open?.id} doc={open} onClose={() => setOpen(null)} onRotated={list.reload} onDeleted={() => { setOpen(null); list.reload(); }} />
    </div>
  );
}
